import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

/**
 * Side sheet (POS-I238). A small self-contained portal, added with the record viewer because
 * PrimeReact's Sidebar cannot sit above a PrimeReact Dialog: its global Escape handler runs only
 * the highest-priority listener (DIALOG 300 > SIDEBAR 100), so Escape would close the dialog
 * behind and leave the sidebar open.
 *
 * - role=dialog aria-modal; overlay z-[2000], panel z-[2001] (PrimeReact modals start at ~1100;
 *   react-hot-toast sits at 9999 so toasts stay visible above the sheet).
 * - Opens on the inline-end side: right in LTR, left in Urdu, with the slide-in mirrored.
 * - Own Tab loop and own Escape: a window keydown listener in the CAPTURE phase calls
 *   preventDefault + stopImmediatePropagation, so PrimeReact's document listener never fires and
 *   the dialog behind keeps its values. It exists only while the sheet is open.
 * - On close, focus returns to `returnFocusTo`, else to the element focused when it opened.
 */
const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  /** id of the heading element inside the sheet (aria-labelledby). */
  labelledBy: string;
  children: ReactNode;
  returnFocusTo?: HTMLElement | null;
  className?: string;
  testId?: string;
}

export function Sheet({ open, onClose, labelledBy, children, returnFocusTo, className, testId }: SheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const returnRef = useRef(returnFocusTo);
  returnRef.current = returnFocusTo;

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    // Initial focus: the Close button (marked data-sheet-initial) or the first focusable.
    const first = panel?.querySelector<HTMLElement>('[data-sheet-initial]') ?? panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panel) return;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (items.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (!panel.contains(active)) {
        e.preventDefault();
        firstEl.focus();
      } else if (e.shiftKey && active === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && active === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      const target = returnRef.current?.isConnected ? returnRef.current : opener?.isConnected ? opener : null;
      target?.focus();
    };
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div data-testid={testId}>
      <div className="fixed inset-0 z-[2000] bg-scrim/40" onClick={() => onCloseRef.current()} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className={cn(
          'fixed inset-y-0 end-0 z-[2001] flex w-full flex-col border-s bg-background shadow-xl outline-none sm:max-w-2xl',
          'animate-in duration-200 slide-in-from-right rtl:slide-in-from-left',
          className,
        )}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
